import {
  Alert,
  Button,
  Center,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback } from "react";
import {
  useAmendmentDraft,
  useAmendmentOptions,
} from "../../hooks/useAmendment";
import { AmendmentEditorFormModal } from "./AmendmentEditorFormModal";

export interface AmendmentEditorModalProps {
  opened: boolean;
  caseId: string;
  caseNumber: string;
  sessionId?: string | null;
  onClose: () => void;
  onSigned: () => void;
  onSessionUpdated?: (sessionId: string, expiresAt?: string) => void;
  reviewRequired?: boolean;
}

export function AmendmentEditorModal({
  opened,
  caseId,
  caseNumber,
  sessionId,
  onClose,
  onSigned,
  onSessionUpdated,
  reviewRequired = true,
}: AmendmentEditorModalProps) {
  const draftQuery = useAmendmentDraft(caseId, opened);
  const optionsQuery = useAmendmentOptions(caseId, opened);
  const isLoading =
    draftQuery.data === undefined || optionsQuery.data === undefined;
  const hasError = draftQuery.isError || optionsQuery.isError;

  const handleRetry = useCallback(async () => {
    await Promise.all([draftQuery.refetch(), optionsQuery.refetch()]);
  }, [draftQuery, optionsQuery]);

  if (isLoading || hasError) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={`Add Amendment — ${caseNumber}`}
        size="90vw"
        centered
        closeOnClickOutside={false}
        closeButtonProps={{ "aria-label": "Close amendment editor" }}
      >
        {hasError ? (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="red"
            title="Amendment could not be loaded"
            role="alert"
          >
            <Stack gap="sm" align="flex-start">
              <Text size="sm">
                Refresh the amendment details before editing this signed report.
              </Text>
              <Button variant="light" color="red" onClick={handleRetry}>
                Retry
              </Button>
            </Stack>
          </Alert>
        ) : (
          <Center py="xl">
            <Loader />
          </Center>
        )}
      </Modal>
    );
  }

  return (
    <AmendmentEditorFormModal
      key={`${caseId}-${opened ? "open" : "closed"}`}
      opened={opened}
      caseId={caseId}
      caseNumber={caseNumber}
      sessionId={sessionId}
      initialDraft={draftQuery.data ?? null}
      options={optionsQuery.data}
      onClose={onClose}
      onSigned={onSigned}
      onSessionUpdated={onSessionUpdated}
      reviewRequired={reviewRequired}
    />
  );
}
